'use client';
import React from 'react';
import { MosaicNode } from 'react-mosaic-component';

const compareWorkspaceConfigs = (
  prev: MosaicNode<string> | null | undefined,
  next: MosaicNode<string> | null | undefined,
): boolean => {
  if (typeof prev === 'object' && typeof next === 'object') {
    if (prev === null && next === null) {
      return true;
    } else {
      return compareWorkspaceConfigs(prev?.first, next?.first) && compareWorkspaceConfigs(prev?.second, next?.second);
    }
  } else {
    return prev === next;
  }
};

export interface WorkspaceContextProps {
  config: MosaicNode<string> | null;
  configWidgetsLocationsChanged: boolean;
  isActiveWorkspace: boolean;
  isWidgetPositionChanging: boolean;
}

export const WorkspaceContext = React.createContext<WorkspaceContextProps>({
  config: null,
  configWidgetsLocationsChanged: false,
  isActiveWorkspace: true,
  isWidgetPositionChanging: false,
});

export const WorkspaceContextProvider: React.FC<
  React.PropsWithChildren<Partial<Omit<WorkspaceContextProps, 'configWidgetsLocationsChanged'>>>
> = props => {
  const [prevWorkspaceConfig, setPrevWorkspaceConfig] = React.useState<MosaicNode<string> | null | undefined>(
    undefined,
  );

  const value = React.useMemo(
    () => ({
      config: props.config ?? null,
      configWidgetsLocationsChanged: !compareWorkspaceConfigs(props.config, prevWorkspaceConfig),
      isActiveWorkspace: props.isActiveWorkspace ?? false,
      isWidgetPositionChanging: props.isWidgetPositionChanging ?? false,
    }),
    [prevWorkspaceConfig, props.config, props.isActiveWorkspace, props.isWidgetPositionChanging],
  );

  React.useEffect(() => {
    setPrevWorkspaceConfig(props.config);
  }, [props.config]);

  return <WorkspaceContext.Provider value={value}>{props.children}</WorkspaceContext.Provider>;
};
